import React, { useState, useEffect } from 'react';
import { DollarSign, Download, Clock, CheckCircle, ArrowUpRight } from 'lucide-react';
import { getRegistryFinancials, getWithdrawalRequests } from '../lib/withdrawal';
import MoneyRequestModal from './MoneyRequestModal';
import type { RegistryFinancials, WithdrawalRequest } from '../types/withdrawal';

interface Props {
  registryId: string;
  currency: string;
}

export default function MoneyRequestSection({ registryId, currency }: Props) {
  const [financials, setFinancials] = useState<RegistryFinancials | null>(null);
  const [withdrawalRequests, setWithdrawalRequests] = useState<WithdrawalRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, [registryId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const [financialsData, requestsData] = await Promise.all([
        getRegistryFinancials(registryId),
        getWithdrawalRequests(registryId)
      ]);

      setFinancials(financialsData);
      setWithdrawalRequests(requestsData);
    } catch (err) {
      console.error('Error loading money request data:', err);
      setError('Failed to load financial information');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-400" />;
      case 'approved':
        return <CheckCircle className="w-4 h-4 text-blue-400" />;
      case 'transferred':
        return <CheckCircle className="w-4 h-4 text-green-400" />;
      default:
        return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-900/50 text-yellow-200';
      case 'approved':
        return 'bg-blue-900/50 text-blue-200';
      case 'transferred':
        return 'bg-green-900/50 text-green-200';
      default:
        return 'bg-gray-900/50 text-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6">
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6">
        <div className="text-center py-8">
          <p className="text-red-400">{error}</p>
          <button
            onClick={loadData}
            className="mt-4 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <DollarSign className="w-6 h-6 text-white" />
          <h2 className="text-xl font-semibold text-white">Money Requests</h2>
        </div>
        {financials && financials.available_balance > 0 && (
          <button
            onClick={() => setShowRequestModal(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-600 transition-colors"
          >
            <ArrowUpRight className="w-4 h-4" />
            <span>Request Money</span>
          </button>
        )}
      </div>

      {financials && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gray-900/50 p-4 rounded-lg">
            <p className="text-sm text-gray-400 mb-1">Total Collected</p>
            <p className="text-xl font-bold text-white">
              {currency} {financials.total_collected.toFixed(2)}
            </p>
          </div>
          <div className="bg-gray-900/50 p-4 rounded-lg">
            <p className="text-sm text-gray-400 mb-1">Total Withdrawn</p>
            <p className="text-xl font-bold text-white">
              {currency} {financials.total_withdrawn.toFixed(2)}
            </p>
          </div>
          <div className="bg-gray-900/50 p-4 rounded-lg">
            <p className="text-sm text-gray-400 mb-1">Pending Requests</p>
            <p className="text-xl font-bold text-yellow-400">
              {currency} {financials.pending_requests.toFixed(2)}
            </p>
          </div>
          <div className="bg-gray-900/50 p-4 rounded-lg">
            <p className="text-sm text-gray-400 mb-1">Available Balance</p>
            <p className="text-xl font-bold text-green-400">
              {currency} {financials.available_balance.toFixed(2)}
            </p>
          </div>
        </div>
      )}

      {/* Withdrawal Requests History */}
        {withdrawalRequests.length === 0 ? (
          <div className="text-center py-8">
            <DollarSign className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-400">No withdrawal requests yet</p>
            {financials && financials.available_balance > 0 && (
              <button
                onClick={() => setShowRequestModal(true)}
                className="mt-4 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-600"
              >
                Make Your First Request
              </button>
            )}
            {financials && financials.available_balance <= 0 && (
              <p className="mt-4 text-sm text-gray-400">
                No funds available for withdrawal yet. Funds will appear here once gifts are claimed.
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {withdrawalRequests.map((request) => (
              <div
                key={request.id}
                className="flex items-center justify-between p-4 bg-gray-900/50 rounded-lg"
              >
                <div className="flex items-center space-x-4">
                  {getStatusIcon(request.status)}
                  <div>
                    <p className="font-medium text-white">
                      {currency} {request.amount.toFixed(2)}
                    </p>
                    <p className="text-sm text-gray-400">
                      Requested on {new Date(request.requested_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(request.status)}`}>
                    {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                  </span>
                  {request.processed_at && (
                    <p className="text-xs text-gray-400 mt-1">
                      Processed: {new Date(request.processed_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showRequestModal && financials && (
        <MoneyRequestModal
          registryId={registryId}
          currency={currency}
          onClose={() => setShowRequestModal(false)}
          onSuccess={loadData}
        />
      )}
    </>
  );
}